import { useCallback, useEffect, useMemo, useRef, useState } from "react";

const DEFAULT_PAGE_SIZE = 20;
const LOAD_MORE_THRESHOLD_PX = 120;

function rowFocusId(row) {
  if (!row) return "";
  if (row.focusId) return String(row.focusId);
  if (row._id) return String(row._id);
  if (row.id) return String(row.id);
  return "";
}

function getUrlFocusId() {
  try {
    return new URLSearchParams(window.location.search || "").get("focusId") || "";
  } catch {
    return "";
  }
}

function countForFocus(rows, pageSize) {
  const focusId = getUrlFocusId();
  if (!focusId) return pageSize;
  const index = rows.findIndex((row) => rowFocusId(row) === focusId);
  if (index < 0) return pageSize;
  return Math.ceil((index + 1) / pageSize) * pageSize;
}

export default function useIncrementalRows(rows = [], options = {}) {
  const pageSize = Number(options.pageSize || DEFAULT_PAGE_SIZE);
  const safeRows = Array.isArray(rows) ? rows : [];
  const shellRef = useRef(null);
  const focusToken = typeof window !== "undefined" ? window.location.search : "";
  const [visibleCount, setVisibleCount] = useState(() => Math.max(pageSize, countForFocus(safeRows, pageSize)));

  useEffect(() => {
    const nextCount = Math.max(pageSize, countForFocus(safeRows, pageSize));
    setVisibleCount(Math.min(Math.max(nextCount, pageSize), Math.max(safeRows.length, pageSize)));
    if (shellRef.current) shellRef.current.scrollTop = 0;
  }, [safeRows, pageSize, focusToken]);

  const visibleRows = useMemo(() => safeRows.slice(0, visibleCount), [safeRows, visibleCount]);
  const hasMore = visibleCount < safeRows.length;

  const loadMore = useCallback(() => {
    setVisibleCount((current) => Math.min(current + pageSize, safeRows.length));
  }, [pageSize, safeRows.length]);

  const handleScroll = useCallback((event) => {
    if (!hasMore) return;
    const element = event.currentTarget;
    const bottomDistance = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (bottomDistance <= LOAD_MORE_THRESHOLD_PX) loadMore();
  }, [hasMore, loadMore]);

  return {
    shellRef,
    rows: visibleRows,
    totalCount: safeRows.length,
    visibleCount: Math.min(visibleCount, safeRows.length),
    hasMore,
    handleScroll,
  };
}
